import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Me } from '../lib/api.ts';

const navKlasse = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
    isActive ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-200'
  }`;

interface TenantValg {
  id: string;
  navn: string;
}

export default function Layout({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const erSuperadmin = me.rolle === 'sysadmin';

  // Superadmin kan bytte til enhver tenant; vanlige brukere til sine medlemskap
  const alleTenantsQuery = useQuery<TenantValg[]>({
    queryKey: ['tenants-valg'],
    queryFn: () => api.get('/system/tenants'),
    enabled: erSuperadmin,
  });
  const tenantValg: TenantValg[] = erSuperadmin
    ? (alleTenantsQuery.data ?? [])
    : me.tenants;

  async function loggUt() {
    await api.post('/auth/logout').catch(() => undefined);
    qc.setQueryData(['me'], null);
    qc.clear();
    window.location.href = '/';
  }

  async function byttTenant(tenantId: string) {
    if (!tenantId || tenantId === me.tenantId) return;
    await api.post('/auth/switch-tenant', { tenantId }).catch(() => undefined);
    qc.clear();
    window.location.href = '/';
  }

  const visTenantMeny = !!me.tenantId || !erSuperadmin;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <span className="text-lg font-bold text-slate-800">⚡ Billading</span>
          <nav className="flex flex-1 flex-wrap gap-1">
            {visTenantMeny && (
              <>
                <NavLink to="/" end className={navKlasse}>
                  Dashbord
                </NavLink>
                <NavLink to="/ladere" className={navKlasse}>
                  Ladere
                </NavLink>
                <NavLink to="/okonomi" className={navKlasse}>
                  Økonomi
                </NavLink>
                {(me.rolle === 'admin' || erSuperadmin) && (
                  <NavLink to="/register" className={navKlasse}>
                    Register
                  </NavLink>
                )}
              </>
            )}
            {erSuperadmin && (
              <NavLink to="/system" className={navKlasse}>
                System
              </NavLink>
            )}
          </nav>
          <div className="flex items-center gap-3 text-sm">
            {(erSuperadmin && tenantValg.length > 0) || tenantValg.length > 1 ? (
              <select
                value={me.tenantId ?? ''}
                onChange={(e) => byttTenant(e.target.value)}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
                title="Bytt tenant"
              >
                {!me.tenantId && <option value="">– velg tenant –</option>}
                {tenantValg.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.navn}
                  </option>
                ))}
              </select>
            ) : (
              me.tenantNavn && <span className="hidden text-slate-500 sm:inline">{me.tenantNavn}</span>
            )}
            <span className="hidden text-slate-500 sm:inline">
              {me.navn}
              {erSuperadmin ? ' (superadmin)' : ''}
            </span>
            <button
              onClick={loggUt}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            >
              Logg ut
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">{children}</main>

      <footer className="border-t border-slate-200 bg-white py-3 text-center text-xs text-slate-400">
        Strømpriser levert av{' '}
        <a
          href="https://www.hvakosterstrommen.no"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-slate-600"
        >
          Hva koster strømmen.no
        </a>
      </footer>
    </div>
  );
}
